import {ProjectRoleRes, ProjectSummaryGroupingRes} from "../project.contract";
import {TrackerDetailRes} from "./tracker.contract";
import {ToDoGroupRes} from "../todo.contract";
import {Type} from 'class-transformer';
import {InvoiceDetailRes} from '../../cp/invoice.contract';
import {BaseTaskRes, EffortType} from '../task';
import {UsedTimeRes} from '../../resource-manager/used-time';
import moment from "moment";
import {
    ColumnType,
    FilterType,
    yascClassDisplayName,
    yascDisplayName,
    yascFilterConfig,
    yascFormGrid,
    yascFormIgnore,
    YascOptionItem,
    yascRequired,
    yascTableIgnore,
    yascTableIndex
} from '@yasc/yasc-material-components';
import {numberWithCommas} from '../../../../../apps/apps.component';
import {CompanyDetailsRes, CompanyMinimumRes} from '../../cp';
import {WorkflowDetailRes} from '../workflow.contract';
import {yascTableColumn} from '@yasc/yasc-material-components';
import {
    yascRequestOverride,
    yascTableDisplayFn, yascTableFilterType, yascTableLevelOfDetail
} from '@yasc/yasc-material-components';

@yascClassDisplayName('projectTableTitle')
export class ProjectTrackerRes extends TrackerDetailRes {
    @yascDisplayName('general_currentWorkflow')
    @yascFormGrid({ row: 4, width: 2 })
    @yascTableIgnore(true)
    @yascTableColumn(ColumnType.Select)
    @yascRequestOverride('workflowId')
    @Type(() => WorkflowDetailRes)
    workflow!: WorkflowDetailRes;

    @yascDisplayName('projectTableVolume')
    @yascTableDisplayFn((value: number) => numberWithCommas(value))
    @yascTableIndex(10)
    @yascFormGrid({ row: 5, width: 3 })
    @yascRequired()
    @yascTableLevelOfDetail(1)
    projectVolumeBrutto!: number;

    @yascDisplayName('projectTableRemainingAmount')
    @yascTableDisplayFn((value: number) => numberWithCommas(value))
    @yascTableIndex(11)
    @yascFormGrid({ row: 5, width: 1 })
    @yascTableLevelOfDetail(1)
    projectVolumeBruttoOpen!: number;

    @yascDisplayName('projectTableOfferNr')
    @yascTableIndex(2)
    @yascFormGrid({ row: 0, width: 2 })
    offerNr?: string;

    @yascDisplayName('projectTableOrderNr')
    @yascTableIndex(1)
    @yascFormGrid({ row: 0, width: 2 })
    orderNr?: string;

    @yascTableIgnore(true)
    @yascDisplayName('general_description')
    @yascFormGrid({ row: 2 })
    @yascTableLevelOfDetail(3)
    comment!: string;

    private _nextBill: any;

    @yascDisplayName('crm_projects_nextBill_tableHeader')
    @yascTableIndex(12)
    @yascTableColumn(ColumnType.Date)
    @yascTableLevelOfDetail(3)
    get nextBill(): any {
        return this._nextBill;
    }

    set nextBill(value: any) {
        this._nextBill = value ? moment(value) : null;
    }

    @Type(() => CompanyDetailsRes)
    @yascDisplayName('projectTableCustomer')
    @yascTableColumn(ColumnType.Select)
    @yascTableDisplayFn((value?: CompanyDetailsRes) => value?.name ?? '')
    @yascFormGrid({ row: 3 })
    @yascRequestOverride('customerId')
    @yascTableIndex(0)
    @yascTableFilterType(FilterType.autocomplete)
    customer!: CompanyDetailsRes | CompanyMinimumRes;

    @Type(() => InvoiceDetailRes)
    invoices: InvoiceDetailRes[] = [];

    @Type(() => ProjectRoleRes)
    teamMembers: ProjectRoleRes[] = [];

    @Type(() => ToDoGroupRes)
    toDos: ToDoGroupRes | null = null;

    @Type(() => BaseTaskRes)
    tasks: BaseTaskRes[] = [];

    @Type(() => UsedTimeRes)
    usedTime: UsedTimeRes[] = [];

    @Type(() => EffortType)
    allowedEffortTypes: EffortType[] = [];

    sleeper!: boolean;

    color?: string;

    toMinRes() {
        const minRes = new ProjectMinRes();
        minRes.id = this.id;
        minRes.name = this.name;
        minRes.type = this.type;
        minRes.isActive = this.isActive;
        minRes.currentWorkflowStatus = this.currentWorkflowStatus;
        minRes.statusHistory = this.statusHistory;
        minRes.notes = this.notes;

        minRes.offerNr = this.offerNr;
        minRes.orderNr = this.orderNr;
        minRes.comment = this.comment;
        minRes.teamMemberCount = this.teamMembers?.length ?? 0;
        minRes.sleeper = this.sleeper;
        minRes.color = this.color;
        minRes.nextBill = this.nextBill;
        minRes.projectVolumeBrutto = this.projectVolumeBrutto;
        minRes.projectVolumeBruttoOpen = this.projectVolumeBruttoOpen;

        if (this.customer) {
            const customer = new CompanyMinimumRes();
            customer.id = this.customer.id;
            customer.name = this.customer.name;
            minRes.customer = customer;
        }

        return minRes;
    }
}

@yascClassDisplayName('projectTableTitle')
export class ProjectMinRes extends TrackerDetailRes {
    @Type(() => CompanyMinimumRes)
    @yascDisplayName('projectTableCustomer')
    @yascTableColumn(ColumnType.Select)
    @yascTableDisplayFn((value?: CompanyMinimumRes) => value?.name)
    @yascFormGrid({ row: 0, width: 2 })
    @yascRequestOverride('customerId')
    @yascTableFilterType(FilterType.autocomplete)
    @yascTableIndex(1)
    @yascFilterConfig({
        getGroup: (value?: ProjectSummaryGroupingRes) => value?.availableCompanies ?? [],
        mapToFilter: (value?: CompanyMinimumRes) =>
            value ? new YascOptionItem(value.name, value.id) : undefined as any
    })
    customer!: CompanyMinimumRes;

    @yascDisplayName('projectTableOrderNr')
    @yascTableIndex(2)
    @yascFormGrid({ row: 0, width: 1 })
    orderNr?: string;

    @yascDisplayName('projectTableOfferNr')
    @yascTableIndex(3)
    @yascFormGrid({ row: 0, width: 1 })
    offerNr?: string;

    @yascTableIgnore(true)
    @yascDisplayName('general_description')
    @yascFormGrid({ row: 2, width: 4 })
    @yascTableLevelOfDetail(3)
    comment?: string;

    @yascDisplayName('projectTableTeamMembers')
    @yascTableIgnore(true)
    @yascFormGrid({ row: 1, width: 1 })
    teamMemberCount!: number;

    private _nextBill: any;

    @yascDisplayName('crm_projects_nextBill_tableHeader')
    @yascTableIndex(4)
    @yascTableColumn(ColumnType.Date)
    @yascTableLevelOfDetail(2)
    get nextBill(): any {
        return this._nextBill;
    }

    set nextBill(value: any) {
        this._nextBill = value ? moment(value) : null;
    }

    @yascDisplayName('projectTableVolume')
    @yascTableDisplayFn((value: number) => numberWithCommas(value))
    @yascTableIndex(5)
    @yascFormGrid({ row: 3, width: 2 })
    @yascTableLevelOfDetail(1)
    projectVolumeBrutto!: number;

    @yascDisplayName('projectTableRemainingAmount')
    @yascTableDisplayFn((value: number) => numberWithCommas(value))
    @yascTableIndex(6)
    @yascFormGrid({ row: 3, width: 2 })
    @yascTableLevelOfDetail(1)
    projectVolumeBruttoOpen!: number;

    @yascTableIgnore(true)
    @yascFormIgnore()
    sleeper!: boolean;

    @yascDisplayName('general_color')
    @yascFormGrid({ row: 1, width: 1 })
    @yascTableLevelOfDetail(2)
    @yascTableIgnore(true)
    color?: string;
}